import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000IdColumnDef, type U2000CriteriaLookupConfig, type U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useEffect, useMemo, useRef } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU3125Context } from "../U3125Context";
import { useU3125InitCreateInvoiceDialog } from "../U3125InitCreateInvoiceDialogContext";
import { doDeleteData, doExportExcel, doSearch, luHandleAppiError, pickInvoiceTypeLabel, u3125FormatInvoiceNumberDisplay, u3125InvoiceListSubtotal, u3125InvoiceListTaxTotal, u3125InvoiceListTotal, type U3125DocumentNatureInitItem, type U3125InvoiceSearchRow, type U3125InvoiceTypeInitItem } from "../U3125Service";
import { NS, U3125ROLES, u3125FormatCurrency } from "../U3125";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U3125FactureDeleteDlg from "./common/U3125FactureDeleteDlg";
import { U3125FactureListToolBar } from "./list/U3125FactureListToolBar";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";
import {
  U2000_CRITERIA_LOOKUP_FIND_INVOICE,
  U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
  U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import {
  U2000_CRITERIA_INIT_OPTIONS_U3125_DOCUMENT_NATURES,
  registerCriteriaInitOptionsResolver,
  unregisterCriteriaInitOptionsResolver,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaInitOptionsRegistry";
import {
  buildU3125DocumentNatureCriteriaOptions,
  u3125BuildDocumentNatureListColumn,
} from "../tools/U3125DocumentNatureCriteria";
import {
  u3125InvoiceLookupItemToDisplay,
  u3125InvoiceLookupItemToSelectedDisplay,
  u3125InvoiceLookupItemToValue,
  useU3125FindInvoiceHandlers,
} from "../U3125UseFindInvoiceHandlers";
import { useU3125FindProjectHandlers } from "../U3125UseFindProjectHandlers";
import { useU3121FindCustomerHandlers } from "../../u3121Project/U3121UseFindHandlers";
import {
  U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION,
  u3121FormatProjectLookupLabel,
} from "../../u3121Project/U3121Service";

export function useU3125FactureListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

/** Évite les colonnes dupliquées (ex. filtre localStorage obsolète). */
function u3125DedupeFieldNames(names: string[]): string[] {
  return [...new Set(names)];
}

/** Affichage findCustomer : `customerDisplay` (code - nom) ou repli sur code/nom. */
function u3125CustomerLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig): string {
  if (!item || typeof item !== "object") return "";
  const rec = item as Record<string, unknown>;
  const display = String(rec.customerDisplay ?? "").trim();
  if (display) return display;
  const code = String(rec.customerCode ?? "").trim();
  const name = String(rec.customerName ?? "").trim();
  if (code && name) return `${code} - ${name}`;
  return code || name || "";
}

function u3125CustomerLookupItemToSelectedDisplay(item: unknown, config: U2000CriteriaLookupConfig): string {
  return u3125CustomerLookupItemToDisplay(item, config);
}

function u3125FormatCustomerGridDisplay(c: { customerCode?: string; customerName?: string; customerDisplay?: string } | null | undefined): string {
  if (!c) return "";
  const display = c.customerDisplay?.trim();
  if (display) return display;
  const code = c.customerCode?.trim() ?? "";
  const name = c.customerName?.trim() ?? "";
  if (code && name) return `${code} - ${name}`;
  return code || name || "";
}

/** Affichage findProject : `projetNumber — projetName` (+ suffixe inactif si projectStatus === false). */
function u3125ProjectLookupItemToDisplay(item: unknown, _config: U2000CriteriaLookupConfig, inactiveSuffix: string): string {
  return u3121FormatProjectLookupLabel(item as Record<string, unknown>, inactiveSuffix);
}

function u3125ProjectLookupItemToSelectedDisplay(
  item: unknown,
  config: U2000CriteriaLookupConfig,
  inactiveSuffix: string,
): string {
  return u3125ProjectLookupItemToDisplay(item, config, inactiveSuffix);
}

function u3125FormatProjectGridDisplay(p: { projetNumber?: string; projetName?: string; id?: string; projectDisplay?: string; projetDisplay?: string } | null | undefined): string {
  if (!p) return "";
  const display = (p.projectDisplay ?? p.projetDisplay)?.trim();
  if (display) return display;
  const num = p.projetNumber?.trim() ?? "";
  const name = p.projetName?.trim() ?? "";
  if (num && name) return `${num} — ${name}`;
  return num || name || p.id || "";
}

function getColumnsDef(
  t: TFunction,
  lang: string,
  invoiceTypes: U3125InvoiceTypeInitItem[],
  documentNatures: U3125DocumentNatureInitItem[],
): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "invoiceNumber",
      label: t("lblInvoice", { ns: NS }),
      widthClass: "w-[100px]",
      type: "string",
      align: "right",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_FIND_INVOICE,
        searchCriterionField: "invoiceNumber",
        valueKey: "invoiceNumber",
        displayKeys: ["invoiceNumber"],
        placeholder: t("phCriteriaInvoice", { ns: "common" }),
      },
      display: (_c, v) => u3125FormatInvoiceNumberDisplay(v as string | null, t("lblInvoiceNumberAutoHint", { ns: NS })),
    },
    {
      name: "project",
      label: t("lblProject", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      display: (_column, value) =>
        u3125FormatProjectGridDisplay(value as { projetNumber?: string; projetName?: string; id?: string } | null | undefined),
    },
    {
      name: "project.projetNumber",
      label: t("lblProject", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
        searchCriterionField: "projetNumber",
        valueKey: "projetNumber",
        criteriaField: "project.projetNumber",
        displayKeys: ["projetNumber", "projetName"],
        selectedDisplayKeys: ["projetNumber", "projetName"],
        placeholder: t("phCriteriaProject", { ns: "common" }),
      },
    },
    {
      name: "project.customer",
      label: t("lblCustomer", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: false,
      sortable: false,
      isDefaultSortedColumn: false,
      display: (_column, value) =>
        u3125FormatCustomerGridDisplay(value as { customerCode?: string; customerName?: string; customerDisplay?: string } | null | undefined),
    },
    {
      name: "project.customer.customerCode",
      label: t("lblCustomer", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: false,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      includeConditions: ["equals", "in"],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER,
        searchCriterionField: "search",
        valueKey: "customerCode",
        criteriaField: "project.customer.customerCode",
        displayKeys: ["customerDisplay"],
        selectedDisplayKeys: ["customerDisplay"],
        placeholder: t("phCriteriaCustomer", { ns: "common" }),
      },
    },
    {
      name: "invoiceDate",
      label: t("colInvoiceDate", { ns: NS }),
      widthClass: "w-[160px]",
      type: "date",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: "DESC",
    },
    {
      name: "status",
      label: t("colInvoiceStatus", { ns: NS }),
      widthClass: "w-[140px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      display: (_c, value) => t(`invoiceStatus_${String(value)}`, { ns: NS, defaultValue: String(value ?? "") }),
    },
    u3125BuildDocumentNatureListColumn(t, documentNatures),
    {
      name: "invoiceTypeCode",
      label: t("colInvoiceTypeCode", { ns: NS }),
      widthClass: "w-[220px]",
      type: "string",
      align: "left",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_c, value) => {
        const code = String(value ?? "").trim();
        if (!code) return "";
        const label = pickInvoiceTypeLabel(invoiceTypes, code, lang);
        return label !== code ? `${label} (${code})` : code;
      },
    },
    {
      name: "amountInvoiceTaxable",
      label: t("colAmountInvoiceTaxable", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "amountInvoiceNonTaxable",
      label: t("colAmountInvoiceNonTaxable", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_c, v) => u3125FormatCurrency(v),
    },
    {
      name: "invoiceSubtotal",
      label: t("colInvoiceSubtotal", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      display: (_c, _v, row) =>
        u3125FormatCurrency(
          u3125InvoiceListSubtotal({
            amountInvoiceTaxable: (row as U3125InvoiceSearchRow)?.amountInvoiceTaxable,
            amountInvoiceNonTaxable: (row as U3125InvoiceSearchRow)?.amountInvoiceNonTaxable,
          }),
        ),
    },
    {
      name: "invoiceTaxTotal",
      label: t("colInvoiceTaxTotal", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      display: (_c, _v, row) =>
        u3125FormatCurrency(
          u3125InvoiceListTaxTotal({
            taxAmount1: (row as U3125InvoiceSearchRow)?.taxAmount1,
            taxAmount2: (row as U3125InvoiceSearchRow)?.taxAmount2,
          }),
        ),
    },
    {
      name: "invoiceTotal",
      label: t("colInvoiceTotal", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: false,
      inGridField: true,
      isDefaultColumn: true,
      sortable: false,
      display: (_c, _v, row) =>
        u3125FormatCurrency(
          u3125InvoiceListTotal({
            amountInvoiceTaxable: (row as U3125InvoiceSearchRow)?.amountInvoiceTaxable,
            amountInvoiceNonTaxable: (row as U3125InvoiceSearchRow)?.amountInvoiceNonTaxable,
            taxAmount1: (row as U3125InvoiceSearchRow)?.taxAmount1,
            taxAmount2: (row as U3125InvoiceSearchRow)?.taxAmount2,
          }),
        ),
    },
    {
      name: "amountTec",
      label: t("colAmountTec", { ns: NS }),
      widthClass: "w-[120px]",
      type: "number",
      align: "right",
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: false,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_c, v) => u3125FormatCurrency(v),
    },
  ];
}

const U3125FactureList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const navigate = useNavigate();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3125Context();
  const { openAddDialog } = useU3125InitCreateInvoiceDialog();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findInvoiceHandlers = useU3125FindInvoiceHandlers();
  const findProjectHandlers = useU3125FindProjectHandlers();
  const findCustomerHandlers = useU3121FindCustomerHandlers();

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_FIND_INVOICE, {
      search: findInvoiceHandlers.search,
      itemToValue: u3125InvoiceLookupItemToValue,
      itemToDisplay: u3125InvoiceLookupItemToDisplay,
      itemToSelectedDisplay: u3125InvoiceLookupItemToSelectedDisplay,
    });
    const inactiveProjectSuffix = t("valInactive", { ns: NS });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT, {
      search: (criterias) =>
        findProjectHandlers.search([...criterias, U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION]),
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3125ProjectLookupItemToDisplay(item, config, inactiveProjectSuffix),
      itemToSelectedDisplay: (item, config) =>
        u3125ProjectLookupItemToSelectedDisplay(item, config, inactiveProjectSuffix),
    });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER, {
      search: findCustomerHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: u3125CustomerLookupItemToDisplay,
      itemToSelectedDisplay: u3125CustomerLookupItemToSelectedDisplay,
    });
    return () => {
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_FIND_INVOICE);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT);
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_CUSTOMER);
    };
  }, [findInvoiceHandlers.search, findProjectHandlers.search, findCustomerHandlers.search, t]);

  const invoiceTypes = useMemo(() => {
    const p = luStateHandlers.getPartialPersonnalization(["invoiceTypes"]) as { invoiceTypes?: U3125InvoiceTypeInitItem[] };
    return p?.invoiceTypes ?? [];
  }, [luStateHandlers]);

  const documentNatures = useMemo(() => {
    const p = luStateHandlers.getPartialPersonnalization(["documentNatures"]) as {
      documentNatures?: U3125DocumentNatureInitItem[];
    };
    return p?.documentNatures ?? [];
  }, [luStateHandlers]);

  useEffect(() => {
    registerCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3125_DOCUMENT_NATURES, () =>
      buildU3125DocumentNatureCriteriaOptions(documentNatures, t),
    );
    return () => {
      unregisterCriteriaInitOptionsResolver(U2000_CRITERIA_INIT_OPTIONS_U3125_DOCUMENT_NATURES);
    };
  }, [documentNatures, t]);

  useEffect(() => {
    const lang = i18n.language || "fr";
    const columns = getColumnsDef(t, lang, invoiceTypes, documentNatures);
    searchStateHandlers.setColumns(columns);
    gridStateHandlers.setColumns(columns);
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t, i18n.language, invoiceTypes, documentNatures]);

  const handleSearch = useU2000SearchHandlers<U3125InvoiceSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => doSearch(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3125InvoiceSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      const uniqueFieldNames = u3125DedupeFieldNames(gridStateHandlers.getSelectedFields());
      if (uniqueFieldNames.length !== gridStateHandlers.getSelectedFields().length) {
        gridStateHandlers.setSelectedFields(uniqueFieldNames);
      }
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3125";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const row = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, row.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  useU2000ExportExcelNotification();

  const u3125FactureListHandlerActions: U2000TableHandlers<U3125InvoiceSearchRow> = useU3125FactureListHandlerActions<U3125InvoiceSearchRow>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      const currentFields = searchStateHandlers.getFields();
      const seen = new Set<string>();
      const dedupedFields = currentFields.filter((item) => {
        const name = item.field?.name;
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      });
      if (dedupedFields.length !== currentFields.length) {
        searchStateHandlers.setFields(dedupedFields);
      }
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (row: U3125InvoiceSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      if (currentIdx !== -1) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`${row.id}`);
    },
    onAdd: () => {
      openAddDialog();
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3125");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => gridStateHandlers.getSelectedRows(),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, { id: row.id, version: row.version ?? 0 });
    },
    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();
      deletedRows.forEach((row) => {
        toast.success(t("msgDeleteSuccess", { ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3125",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <>
      <U3125FactureDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />
      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U3125FactureListToolBar gridStateHandlers={gridStateHandlers as never} handlerActions={u3125FactureListHandlerActions as never} />}
        handlerActions={u3125FactureListHandlerActions}
        label={t("lblInvoiceList", { count: gridStateHandlers.getTotalRows(), ns: NS })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3125ROLES.EXPORT)}
      />
    </>
  );
};

export default U3125FactureList;
